import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { useTray } from "../context/TrayContext";
import { trackCart } from "../lib/track";
import { spiceLabel } from "../lib/spice";
import DatePicker from "./DatePicker";
import FulfillmentToggle from "./FulfillmentToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["Wedding", "Birthday", "Corporate", "Family Gathering", "Religious Event", "Other"];

const empty = { name: "", phone: "", email: "", guests: "", event_type: "", address: "", message: "" };

export const InquiryForm = () => {
  const { list, subtotal, deliveryFee, total, count, clear, eventDate, fulfillment, sessionId } = useTray();
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const captureLead = () => {
    if (!form.name.trim() && !form.email.trim() && !form.phone.trim()) return;
    trackCart({
      name: form.name, email: form.email, phone: form.phone,
      items: list.map(({ name, size, price, qty, spice }) => ({ name, size, price, qty, spice })),
      subtotal, total, fulfillment,
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please add your name, phone and email so we can reach you.");
      return;
    }
    if (fulfillment === "Delivery" && !form.address.trim()) {
      toast.error("Please add a delivery address, or switch to Pickup.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        event_date: eventDate ? format(eventDate, "EEE, MMM d, yyyy") : "",
        fulfillment,
        delivery_address: fulfillment === "Delivery" ? form.address : "",
        items: list.map(({ name, size, price, qty, spice }) => ({ name, size, price, qty, spice })),
        subtotal,
        delivery_fee: deliveryFee,
        estimated_total: total,
        session_id: sessionId,
      });
      setStatus("done");
      toast.success("Your catering request is on its way to Harris & Kanwal!");
      setForm(empty);
      clear();
      setTimeout(() => setStatus("idle"), 4000);
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please call us at 201-620-4376.");
    }
  };

  const field =
    "w-full rounded-xl border-2 border-nosh-border bg-white px-4 py-3 font-body text-nosh-ink placeholder:text-nosh-ink/40 outline-none transition-colors focus:border-nosh-green focus:ring-2 focus:ring-nosh-green/20";
  const labelC = "mb-1.5 block font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-green";

  return (
    <form data-testid="inquiry-form" onSubmit={onSubmit} className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelC} htmlFor="inq-name">Your name *</label>
          <input id="inq-name" data-testid="inquiry-name" className={field} value={form.name} onChange={update("name")} onBlur={captureLead} placeholder="e.g. Ayesha Khan" />
        </div>
        <div>
          <label className={labelC} htmlFor="inq-phone">Phone *</label>
          <input id="inq-phone" data-testid="inquiry-phone" className={field} value={form.phone} onChange={update("phone")} onBlur={captureLead} placeholder="(201) 000-0000" />
        </div>
      </div>

      <div>
        <label className={labelC} htmlFor="inq-email">Email *</label>
        <input id="inq-email" data-testid="inquiry-email" type="email" className={field} value={form.email} onChange={update("email")} onBlur={captureLead} placeholder="you@email.com" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelC}>Pickup or delivery</label>
          <FulfillmentToggle />
        </div>
        <div>
          <label className={labelC}>Event date</label>
          <DatePicker />
        </div>
      </div>

      {fulfillment === "Delivery" && (
        <div>
          <label className={labelC} htmlFor="inq-address">Delivery address *</label>
          <input id="inq-address" data-testid="inquiry-address" className={field} value={form.address} onChange={update("address")} placeholder="Street, city, ZIP (NJ / NY area)" />
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelC} htmlFor="inq-guests">Guests</label>
          <input id="inq-guests" data-testid="inquiry-guests" className={field} value={form.guests} onChange={update("guests")} placeholder="e.g. 50" />
        </div>
        <div>
          <label className={labelC} htmlFor="inq-type">Occasion</label>
          <select id="inq-type" data-testid="inquiry-eventtype" className={field} value={form.event_type} onChange={update("event_type")}>
            <option value="">Select…</option>
            {EVENT_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        </div>
      </div>

      <div>
        <label className={labelC} htmlFor="inq-msg">Anything else?</label>
        <textarea id="inq-msg" data-testid="inquiry-message" rows={4} className={field} value={form.message} onChange={update("message")} placeholder="Tell us about your event, dietary needs, or the dishes you're dreaming of…" />
      </div>

      {count > 0 && (
        <div data-testid="inquiry-tray-summary" className="rounded-xl border border-nosh-border bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-green">
              Your order ({count} {count === 1 ? "item" : "items"} · {fulfillment})
            </span>
            <span className="font-display text-lg font-bold text-nosh-green">${total}</span>
          </div>
          <ul className="space-y-1">
            {list.map((i) => (
              <li key={i.key} className="flex justify-between font-body text-sm text-nosh-ink/70">
                <span>
                  {i.qty} × {i.name} <span className="text-nosh-ink/40">({i.size})</span>
                  <span className="ml-1 text-nosh-gold">· {spiceLabel(i.spice)}</span>
                </span>
                <span>${i.price * i.qty}</span>
              </li>
            ))}
            {deliveryFee > 0 && (
              <li className="flex justify-between font-body text-sm text-nosh-ink/70">
                <span>Delivery fee</span><span>${deliveryFee}</span>
              </li>
            )}
          </ul>
        </div>
      )}

      <motion.button
        type="submit"
        data-testid="inquiry-submit"
        disabled={status === "loading"}
        whileTap={{ scale: 0.98 }}
        className="mt-1 flex items-center justify-center gap-2.5 rounded-full bg-nosh-green px-7 py-4 font-poster text-sm font-bold uppercase tracking-wide text-nosh-cream transition-colors duration-300 hover:bg-nosh-ink disabled:opacity-70"
      >
        {status === "loading" ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
        ) : status === "done" ? (
          <><CheckCircle2 className="h-4 w-4" /> Request sent!</>
        ) : (
          <><Send className="h-4 w-4" /> Send catering request</>
        )}
      </motion.button>

      <p data-testid="payment-note" className="text-center font-body text-xs text-nosh-ink/55">
        We accept <span className="font-semibold text-nosh-green">Zelle</span> &amp;{" "}
        <span className="font-semibold text-nosh-green">Venmo</span> — payment is made in advance once
        your order is confirmed.
      </p>
    </form>
  );
};

export default InquiryForm;
